import Vue from 'vue'
import { PipeDiags, Client, OrgApi, Heartbeat, Queue } from '@/models'

async function tryCatch(obj, asyncFn) {
  obj.isLoading = false
  obj.err = null
  try {
    return await asyncFn()
  } catch (err) {
    obj.err = err
  } finally {
    obj.isLoading = false
  }
}

export default new Vue({
  data() {
    return {
      isLoading: false,
      clientId: null,
      client: null,

      // painel filas
      queues: {
        isLoading: false,
        err: null,
        rows: []
      },

      // painel trechos
      trips: {
        isLoading: false,
        nav: {
          offset: 0,
          limit: 5
        },
        count: 0,
        rows: [],
        err: null
      },

      // último hb via recibo
      lastHbFromReceipt: {
        isLoading: false,
        row: null,
        err: null
      },

      // último hb via último trecho
      lastHbFromLastTrip: {
        isLoading: false,
        row: null,
        err: null
      },

      // HBs que faltam
      hbsToResend: {
        isLoading: false,
        fromHbId: null,
        untilHbId: null,
        rows: [],
        total: 0,
        withRawdata: 0,
        err: null
      }
    }
  },

  methods: {
    setClientId(x) {
      this.clientId = x
      this.trips.offset = 0
      this.$rootLoading(() => this.fetchAll())
    },

    async fetchAll() {
      if (this.clientId == null) throw new Error('Faltou clientId')
      await this.fetchClient()

      // estas duas poderiam ser em paralelo, mas depois da acima
      await this.fetchQueues()
      await this.fetchTrips()

      await this.fetchLastHbFromLastTrip()
      await this.fetchLastHbFromReceipts()

      // Se, depois dos dois finds acima, temos pelo menos um ID
      // de heartbeat, então usa o menor deles para fazer o primeiro
      // fetch de heartbeats.
      let hb0 = this.lastHbFromLastTrip.row
      let hb1 = this.lastHbFromReceipt.row

      if (hb0 != null && hb1 != null) {
        this.hbsToResend.fromHbId = Math.min(hb0.id, hb1.id)
        this.fetchHbsToResend()
      } else if (hb0 != null) {
        this.hbsToResend.fromHbId = hb0.id
        this.fetchHbsToResend()
      } else if (hb1 != null) {
        this.hbsToResend.fromHbId = hb1.id
        this.fetchHbsToResend()
      }
      // else não busca
    },

    async fetchClient() {
      // NB: Já traz client.vehicle a partir do repositório de veículos
      this.client = await Client.findById(this.clientId)
    },

    async fetchTrips() {
      if (this.client.vehicle == null) {
        this.trips.nav = { offset: 0, limit: 5 }
        this.trips.count = 0
        this.trips.rows = []
        this.trips.err = 'Módulo não associado a um veículo'
        return
      }

      await tryCatch(this.trips, async () => {
        let { orgId, idInOrg } = this.client.vehicle
        let { rows, count } = await new OrgApi(orgId).get('trips', {
          vehicleId: idInOrg,
          order: 'createdAt desc, id desc',
          offset: this.trips.nav.offset,
          limit: this.trips.nav.limit
        })

        this.trips.count = count
        this.trips.rows = rows
      })
    },

    paginateTrips({ limit, offset }) {
      this.trips.limit = limit
      this.trips.offset = offset
      this.fetchTrips()
    },

    async fetchQueues() {
      await tryCatch(this.queues, async () => {
        this.queues.rows = await PipeDiags.findFullByClient(this.clientId)
      })
    },

    async restartWorkers() {},

    async fetchLastHbFromReceipts() {
      if (this.client.vehicle == null) {
        this.lastHbFromReceipt.row = null
        this.lastHbFromReceipt.err = 'Módulo sem veículo'
        return
      }

      return await tryCatch(this.lastHbFromReceipt, async () => {
        let orgId = this.client.vehicle.orgId
        let path = `clients/${this.client.id}/heartbeats?limit=1`
        let data = await new OrgApi(orgId).get(path)
        let id = data && data[0] && data[0].heartbeatId
        if (id == null) {
          this.lastHbFromReceipt.err = 'Sem recibo'
          this.lastHbFromReceipt.row = null
        } else {
          this.lastHbFromReceipt.row = await Heartbeat.findById(id)
          this.lastHbFromReceipt.row.type = 'Do último recibo'
          return this.lastHbFromReceipt.row
        }
      })
    },

    async fetchLastHbFromLastTrip() {
      if (this.client.vehicle == null) {
        this.lastHbFromLastTrip.row = null
        this.lastHbFromLastTrip.err = 'Módulo sem veículo'
        return
      }

      return await tryCatch(this.lastHbFromLastTrip, async () => {
        let { idInOrg, orgId } = this.client.vehicle
        let data = await new OrgApi(orgId).get('trips', {
          vehicleId: idInOrg,
          order: 'createdAt desc, id desc',
          limit: 1
        })

        let id = data && data[0] && data[0].lastHeartbeatId
        if (id == null) {
          this.lastHbFromLastTrip.err = 'Sem trecho'
          this.lastHbFromLastTrip.row = null
        } else {
          this.lastHbFromLastTrip.row = await Heartbeat.findById(id)
          this.lastHbFromLastTrip.row.type = 'Do último trecho'
          return this.lastHbFromLastTrip.row
        }
      })
    },

    async fetchHbsToResend() {
      if (!Number.isFinite(this.fromHbId)) {
        this.hbsToResend.rows = []
        return
      }

      await tryCatch(this.hbsToResend, async () => {
        let data = await Heartbeat.findAll({
          clientId: this.client.id,
          limit: 300,
          fromHeartbeat: this.fromHbId,
          untilHeartbeat: this.untilHbId,
          order: 'receivedOn ASC, id ASC'
        })

        this.hbsToResend.rows = data.rows
        this.hbsToResend.total = data.rows.length
        this.hbsToResend.withRawdata = data.rows.filter(
          (r) => r.summary.rawDataLength > 0
        ).length
      })
    },

    async changeFlag(flag, desiredValue) {
      this.$rootLoading(async () => {
        let field = flag == 'paused' ? 'isPaused' : 'isIgnored'
        let body = {}
        body[field] = desiredValue
        await Client.patch(this.client.id, body)
        await this.fetchClient()
      })
    },

    async setRecvPointer(newV) {
      if (!Number.isFinite(newV)) {
        throw new Error('Ponteiro inválido')
      }

      this.$rootLoading(async () => {
        await Client.patch(this.client.id, { lastPointer: newV })
        await this.fetchClient()
      })
    },

    copyLastHbToList(lastHb) {
      if (lastHb != null) {
        this.$refs.theList.setFromHbId(lastHb.id + 1)
        this.$refs.theList.reload()
      }
    },

    async flushQueue(qname) {
      await tryCatch(this.queues, () => Queue.flush(qname))
      this.$snack('Comando enviado')
      this.fetchQueues()
    },

    async restartWorker(body) {
      // orgCode, clientId, extraInfo
      await tryCatch(this.queues, () => PipeDiags.restartWorker(body))
      this.$snack('Comando enviado')
      this.fetchQueues()

      // this.$loading(async () => {
      //   // este delay é um workaround para um bug, onde o $apply
      //   // de um dialog (que faz com que ele feche) acaba fechando
      //   // também o dialog de erro.
      //   await this.$delay(500)
      //   await PipeDiags.restartWorker(body)
      //   await this.fetchData()
      //   this.$snack('Comando enviado')
      // })
    }
  }
})
